import Button from '@bugbug/core/components/Button';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ParallelSubscriptionAddonPlan } from '@bugbug/core/types/subscriptions';
import useAppRoutes from '~/hooks/useAppRoutes';
import useModal from '~/hooks/useModal';
import {
  selectCurrentOrganizationId,
  selectSubscription,
} from '~/modules/organization/organization.selectors';
import { useAppSelector } from '~/modules/store';
import { useGetSubscriptionsPlansQuery } from '~/modules/subscriptions/subscriptions.api';
import analytics, { TRACK_EVENT_ARG_TYPE } from '~/services/analytics';

import { DowngradePlanModal } from '../DowngradePlanModal/DowngradePlanModal';
import { ParallelSelect } from '../ParallelSelect/ParallelSelect';
import { getParallelsLabel } from '../Subscription/Subscription.helpers';
import {
  SubscriptionModalContainer,
  SubscriptionModalContent,
  SubscriptionModalTitle,
  SubscriptionModalDetails,
  SubscriptionModalFooter,
} from '../Subscription/Subscription.styled';

import * as S from './ChangeParallelModal.styled';

import PriceIcon from '~/images/price-icon.svg?react';

interface ChangeParallelModalProps {
  initiallySelectedValue: number;
  currentValue: number;
}

export const ChangeParallelModal = ({
  initiallySelectedValue,
  currentValue,
}: ChangeParallelModalProps) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'changeParallelModal' });
  const { t: tRoot } = useTranslation();
  const modal = useModal();
  const appRoutes = useAppRoutes('subscription');
  const organizationId = useAppSelector(selectCurrentOrganizationId);

  const activeSubscription = useAppSelector(selectSubscription);
  const [selectedParallelRuns, setSelectedParallelRuns] = useState(
    initiallySelectedValue.toString(),
  );
  const { data: plans = [] } = useGetSubscriptionsPlansQuery();
  const subscriptionPlan = plans.find(
    (plan) =>
      plan.type === activeSubscription?.type &&
      plan.billingCycle === activeSubscription?.billingCycle,
  );

  const selectedAddon = subscriptionPlan?.possibleAddons.find(
    (addon): addon is ParallelSubscriptionAddonPlan =>
      addon.type === 'extra_parallel_runs_limit' &&
      addon.value === parseInt(selectedParallelRuns, 10),
  );

  const isUpgrade = parseInt(selectedParallelRuns, 10) > currentValue;

  if (!subscriptionPlan || !activeSubscription) return null;

  const onUpgrade = () => {
    appRoutes.push(
      'checkoutBillingAddress',
      { organizationId },
      {
        type: subscriptionPlan.type,
        billingCycle: subscriptionPlan.billingCycle,
        parallel: selectedParallelRuns,
      },
    );
    modal.hide();
  };

  const onDowngrade = () => {
    analytics.trackEvent('downgrade_button_clicked', {
      [TRACK_EVENT_ARG_TYPE.ORGANIZATION_ID]: organizationId,
      [TRACK_EVENT_ARG_TYPE.ACCOUNT_PLAN]: activeSubscription.type,
      [TRACK_EVENT_ARG_TYPE.PLAN_DOWNGRADED_TO]: activeSubscription.type,
      [TRACK_EVENT_ARG_TYPE.DOWNGRADE_TYPE]: 'parallel_runs_downgrade',
    });

    modal.showCustom(() => (
      <DowngradePlanModal
        activeSubscription={activeSubscription}
        downgradePlan={subscriptionPlan}
        downgradeParallels={parseInt(selectedParallelRuns, 10)}
      />
    ));
  };

  return (
    <SubscriptionModalContainer variant="large">
      <SubscriptionModalContent>
        <S.ChangeParallelIcon />
        <SubscriptionModalDetails>
          <SubscriptionModalTitle>
            {t('title', 'Change number of parallel runs')}
          </SubscriptionModalTitle>
          <div>
            <S.Description>
              {t(
                'description',
                'More parallel runs let you run more tests in the same time. This can help you save time when you work on multiple test suites and continuous development processes.',
              )}
            </S.Description>
            <S.LearnMoreLink
              href={tRoot('default.docs.parallel')}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('learnMore', 'Learn more')}
            </S.LearnMoreLink>
          </div>
          <S.ParallelSelectContainer>
            <S.ParallelSelectLabel>
              {t('selectLabel', 'How many parallel runs do you need?')}
            </S.ParallelSelectLabel>
            <ParallelSelect
              disableCurrentOption
              plan={subscriptionPlan}
              initialParallelRuns={currentValue.toString()}
              selectedParallelRuns={selectedParallelRuns}
              setSelectedParallelRuns={setSelectedParallelRuns}
            />
          </S.ParallelSelectContainer>
          {isUpgrade && selectedAddon && (
            <>
              <S.UpgradeSummaryContainer>
                <PriceIcon />
                <S.UpgradeInfo>
                  {t('upgradeInfo', 'Price for selected number of parallel runs:')}{' '}
                  <S.UpgradePriceValue>
                    ${selectedAddon.pricePerMonth.toString()}{' '}
                    <S.UpgradePriceCycle> / {t('month', 'month')}</S.UpgradePriceCycle>
                  </S.UpgradePriceValue>
                </S.UpgradeInfo>
              </S.UpgradeSummaryContainer>
              <S.UpgradeExtraInfo>
                {t('upgradeExtraInfo', '* You will see exact price calculation in order summary.')}
              </S.UpgradeExtraInfo>
            </>
          )}
          {!isUpgrade && (
            <S.SummaryContainer>
              <S.DowngradeInfoIcon />
              <div>
                <S.DowngradeInfoTitle>
                  {t('downgradeSummaryTitle', 'Downgrading?')}
                </S.DowngradeInfoTitle>
                <S.DowngradeInfoDescription>
                  {t(
                    'downgradeSummaryDescription',
                    'You will still have {{parallelRuns}} until the end of your subscription',
                    { parallelRuns: getParallelsLabel(currentValue) },
                  )}
                </S.DowngradeInfoDescription>
                <S.DowngradeInfoDescription>
                  ({dayjs(activeSubscription.end).format('DD MMM YYYY')})
                </S.DowngradeInfoDescription>
              </div>
            </S.SummaryContainer>
          )}
        </SubscriptionModalDetails>
      </SubscriptionModalContent>
      <SubscriptionModalFooter>
        <Button variant="primary" bordered onClick={modal.hide}>
          {t('cancel', 'Cancel')}
        </Button>
        {isUpgrade ? (
          <Button variant="primary" iconName="arrowRight" iconPosition="right" onClick={onUpgrade}>
            {t('continueUpgrade', 'Continue to order summary')}
          </Button>
        ) : (
          <Button
            variant="primary"
            iconName="arrowRight"
            iconPosition="right"
            onClick={onDowngrade}
          >
            {t('continueDowngrade', 'Continue downgrade')}
          </Button>
        )}
      </SubscriptionModalFooter>
    </SubscriptionModalContainer>
  );
};
